import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { ProductLocation } from '../../shared/models/business.models';
import { SearchSuggestion } from '../../shared/components/form-controls/search-input.component';
import {
  InfiniteScrollComponent,
  InfiniteScrollConfig,
  InfiniteScrollQuery,
  InfiniteScrollResponse,
} from '../../shared/components/ui/infinite-scroll';

export interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  cost?: number; // Custo de aquisição
  stock: number;
  minStock?: number; // Estoque mínimo
  maxStock?: number; // Estoque máximo
  category: string;
  brand: string;
  model: string;
  sku?: string; // Código do produto
  barcode?: string; // Código de barras
  unit: string; // Unidade (peça, kg, metro, etc)
  imageUrl?: string;
  active: boolean;
  locations?: ProductLocation[]; // Localizações do produto
  supplier?: {
    id: number;
    name: string;
  };
  expirationDate?: Date; // Data de validade (para produtos perecíveis)
  serialNumber?: string; // Número de série
  weight?: number; // Peso
  dimensions?: {
    height: number;
    width: number;
    depth: number;
  };
  createdAt: Date;
  updatedAt?: Date;
}

@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InfiniteScrollComponent],
})
export class ProductsComponent implements OnInit {
  // Inject dependencies using Angular 20 pattern
  private fb = inject(FormBuilder);

  // Use signals for reactive state management
  products = signal<Product[]>([]);
  filteredProducts = signal<Product[]>([]);
  searchTerm = signal('');
  selectedCategory = signal('all');
  isLoading = signal(false);

  // Infinite scroll properties
  allProducts: Product[] = []; // Full dataset
  totalProducts = 0;
  hasMoreProducts = true;
  currentPage = 0;
  pageSize = 20;

  // Infinite scroll configuration
  infiniteScrollConfig: InfiniteScrollConfig = {
    pageSize: 20,
    threshold: 200,
    enableSearch: true,
    enableCategoryFilter: true,
    searchPlaceholder: 'Buscar produtos por nome, código ou descrição...',
    searchDebounceTime: 300,
  };

  // Categories for infinite scroll
  productCategories: string[] = [
    'Todos',
    'Eletrônicos',
    'Roupas',
    'Casa',
    'Esporte',
  ];

  // Form for filters
  filterForm: FormGroup;

  // Computed values
  resultCount = computed(() => this.filteredProducts().length);
  hasProducts = computed(() => this.products().length > 0);
  showEmptyState = computed(
    () => !this.isLoading() && this.filteredProducts().length === 0
  );

  displayedColumns: string[] = [
    'image',
    'name',
    'category',
    'price',
    'stock',
    'status',
    'actions',
  ];

  categories = [
    { value: 'all', label: 'Todas as Categorias' },
    { value: 'eletronicos', label: 'Eletrônicos' },
    { value: 'roupas', label: 'Roupas e Acessórios' },
    { value: 'casa', label: 'Casa e Jardim' },
    { value: 'esporte', label: 'Esporte e Lazer' },
  ];

  // Opções simplificadas para select-input
  simpleCategoryOptions = [
    { value: 'all', label: 'Todas as Categorias' },
    { value: 'eletronicos', label: 'Eletrônicos' },
    { value: 'roupas', label: 'Roupas e Acessórios' },
    { value: 'casa', label: 'Casa e Jardim' },
    { value: 'esporte', label: 'Esporte e Lazer' },
  ];

  // Opções para o advanced-select de categorias
  categoryOptions: any[] = [
    { value: 'all', label: 'Todas as Categorias', icon: 'category' },
    { value: 'eletronicos', label: 'Eletrônicos', icon: 'devices' },
    { value: 'roupas', label: 'Roupas e Acessórios', icon: 'checkroom' },
    { value: 'casa', label: 'Casa e Jardim', icon: 'home' },
    { value: 'esporte', label: 'Esporte e Lazer', icon: 'sports' },
  ];

  constructor() {
    // Initialize reactive form
    this.filterForm = this.fb.group({
      search: [''],
      category: ['all'],
    });
  }

  ngOnInit() {
    this.initializeAllProducts();
    this.setupFormSubscriptions();
    // Load initial products using infinite scroll
    this.loadProductsPaginated({ page: 0, pageSize: this.pageSize });
  }

  private setupFormSubscriptions() {
    // Watch for form changes and update signals
    this.filterForm.get('search')?.valueChanges.subscribe((value) => {
      this.searchTerm.set(value || '');
      this.applyFilters();
    });

    this.filterForm.get('category')?.valueChanges.subscribe((value) => {
      this.selectedCategory.set(value || 'all');
      this.applyFilters();
    });
  }

  loadProducts() {
    this.isLoading.set(true);
    // Simulating API call with mock data
    setTimeout(() => {
      this.products.set([
        {
          id: 1,
          name: 'Notebook Dell Inspiron 15',
          description:
            'Notebook para uso profissional com processador Intel i7',
          price: 2899.99,
          cost: 2200.0,
          stock: 15,
          minStock: 5,
          maxStock: 50,
          category: 'eletronicos',
          brand: 'Dell',
          model: 'Inspiron 15 3000',
          sku: 'DELL-INSP-15-001',
          barcode: '7891234567890',
          unit: 'peça',
          active: true,
          locations: [
            {
              productId: 1,
              locationId: 1,
              quantity: 10,
              minQuantity: 3,
              maxQuantity: 30,
              lastUpdated: new Date(),
            },
            {
              productId: 1,
              locationId: 3,
              quantity: 5,
              minQuantity: 2,
              maxQuantity: 10,
              lastUpdated: new Date(),
            },
          ],
          supplier: { id: 1, name: 'Distribuidora Tech Ltda' },
          weight: 2.5,
          dimensions: { height: 2.5, width: 36.0, depth: 24.0 },
          createdAt: new Date('2024-01-15'),
          updatedAt: new Date('2024-06-20'),
        },
        {
          id: 2,
          name: 'Mouse Wireless Logitech',
          description: 'Mouse sem fio com precisão óptica',
          price: 89.9,
          cost: 65.0,
          stock: 2,
          minStock: 5,
          maxStock: 25,
          category: 'eletronicos',
          brand: 'Logitech',
          model: 'MX Master 3',
          sku: 'LOG-MOUSE-001',
          barcode: '7891234567891',
          unit: 'peça',
          active: true,
          locations: [
            {
              productId: 2,
              locationId: 3,
              quantity: 2,
              minQuantity: 3,
              maxQuantity: 15,
              lastUpdated: new Date(),
            },
          ],
          supplier: { id: 1, name: 'Distribuidora Tech Ltda' },
          weight: 0.15,
          dimensions: { height: 4.2, width: 12.6, depth: 8.4 },
          createdAt: new Date('2024-02-10'),
          updatedAt: new Date('2024-06-18'),
        },
        {
          id: 3,
          name: 'Camiseta Polo Lacoste',
          description: 'Camiseta polo masculina 100% algodão',
          price: 199.99,
          cost: 120.0,
          stock: 25,
          minStock: 10,
          maxStock: 100,
          category: 'roupas',
          brand: 'Lacoste',
          model: 'Classic Fit',
          sku: 'LAC-POLO-M-001',
          barcode: '7891234567892',
          unit: 'peça',
          active: true,
          locations: [
            {
              productId: 3,
              locationId: 4,
              quantity: 20,
              minQuantity: 8,
              maxQuantity: 50,
              lastUpdated: new Date(),
            },
            {
              productId: 3,
              locationId: 2,
              quantity: 5,
              minQuantity: 2,
              maxQuantity: 10,
              lastUpdated: new Date(),
            },
          ],
          supplier: { id: 2, name: 'Roupas & Cia Ltda' },
          weight: 0.18,
          dimensions: { height: 1.0, width: 60.0, depth: 45.0 },
          createdAt: new Date('2024-03-05'),
          updatedAt: new Date('2024-06-15'),
        },
        {
          id: 4,
          name: 'Smartphone Samsung Galaxy S24',
          description: 'Smartphone Android com câmera de 108MP',
          price: 3599.99,
          cost: 2800.0,
          stock: 8,
          minStock: 3,
          maxStock: 20,
          category: 'eletronicos',
          brand: 'Samsung',
          model: 'Galaxy S24',
          sku: 'SAM-GAL-S24-001',
          barcode: '7891234567893',
          unit: 'peça',
          active: true,
          locations: [
            {
              productId: 4,
              locationId: 1,
              quantity: 6,
              minQuantity: 2,
              maxQuantity: 15,
              lastUpdated: new Date(),
            },
            {
              productId: 4,
              locationId: 3,
              quantity: 2,
              minQuantity: 1,
              maxQuantity: 5,
              lastUpdated: new Date(),
            },
          ],
          supplier: { id: 1, name: 'Distribuidora Tech Ltda' },
          weight: 0.17,
          dimensions: { height: 0.77, width: 14.7, depth: 7.1 },
          createdAt: new Date('2024-03-20'),
          updatedAt: new Date('2024-06-22'),
        },
      ]);
      this.filteredProducts.set([...this.products()]);
      this.isLoading.set(false);
    }, 1000);
  }

  applyFilters() {
    const filtered = this.products().filter((product) => {
      // Só mostra produtos ativos (não deletados logicamente)
      if (!product.active) return false;

      const matchesSearch =
        product.name.toLowerCase().includes(this.searchTerm().toLowerCase()) ||
        product.description
          .toLowerCase()
          .includes(this.searchTerm().toLowerCase()) ||
        (product.sku &&
          product.sku
            .toLowerCase()
            .includes(this.searchTerm().toLowerCase())) ||
        product.brand.toLowerCase().includes(this.searchTerm().toLowerCase());
      const matchesCategory =
        this.selectedCategory() === 'all' ||
        product.category === this.selectedCategory();

      return matchesSearch && matchesCategory;
    });

    this.filteredProducts.set(filtered);
  }

  editProduct(product: Product) {
    console.log('Editing product:', product);
    // TODO: Open edit dialog or navigate to edit page
  }

  deleteProduct(product: Product) {
    console.log('Deleting product:', product);
    // TODO: Implementar soft delete
    if (
      confirm(`Tem certeza que deseja excluir o produto "${product.name}"?`)
    ) {
      // Soft delete - marca como inativo
      const updatedProducts = this.products().map((p) =>
        p.id === product.id ? { ...p, active: false } : p
      );
      this.products.set(updatedProducts);
      this.applyFilters();
      console.log('Produto marcado como inativo:', product);
    }
  }

  addProduct() {
    console.log('Adding new product');
    // TODO: Open add product dialog or navigate to add page
  }

  viewProduct(product: Product): void {
    console.log('Viewing product:', product);
    // Navigate to product details or open modal
    // this.router.navigate(['/products', product.id]);
  }

  getStockStatus(stock: number): string {
    if (stock === 0) return 'out-of-stock';
    if (stock <= 5) return 'low-stock';
    return 'in-stock';
  }

  getStockStatusText(stock: number): string {
    if (stock === 0) return 'Sem estoque';
    if (stock <= 5) return 'Estoque baixo';
    return 'Em estoque';
  }

  // Métodos para o search-input component
  searchProducts = (query: string): Observable<SearchSuggestion[]> => {
    if (!query || query.length < 2) {
      return of([]);
    }

    // Simula busca no backend com delay
    return of(this.products()).pipe(
      delay(300), // Simula latência da API
      map((products) =>
        products
          .filter(
            (product) =>
              product.active &&
              (product.name.toLowerCase().includes(query.toLowerCase()) ||
                product.description
                  .toLowerCase()
                  .includes(query.toLowerCase()) ||
                product.sku?.toLowerCase().includes(query.toLowerCase()) ||
                product.brand.toLowerCase().includes(query.toLowerCase()))
          )
          .slice(0, 10) // Limita resultados
          .map((product) => ({
            value: product.name,
            label: product.name,
            metadata: {
              id: product.id,
              sku: product.sku,
              price: product.price,
              category: product.category,
            },
          }))
      )
    );
  };

  onSearchChange(searchTerm: string) {
    this.searchTerm.set(searchTerm);
    this.applyFilters();
  }

  onProductSuggestionSelected(suggestion: SearchSuggestion) {
    this.searchTerm.set(suggestion.value);
    this.applyFilters();

    // Opcionalmente, navegar diretamente para o produto
    if (suggestion.metadata?.id) {
      console.log('Produto selecionado:', suggestion.metadata);
    }
  }

  onSearchEnter(searchTerm: string) {
    this.searchTerm.set(searchTerm);
    this.applyFilters();
  }

  // Métodos para o advanced-select component - removido (conflito com infinite scroll)
  // onCategoryChange will be handled by infinite scroll component

  // Infinite scroll methods
  onLoadMore(query: InfiniteScrollQuery): void {
    this.loadProductsPaginated(query);
  }

  onSearch(searchTerm: string): void {
    this.searchTerm.set(searchTerm);
  }

  onCategoryChange(category: string): void {
    this.selectedCategory.set(
      category === 'Todos' ? 'all' : category.toLowerCase()
    );
  }

  private loadProductsPaginated(query: InfiniteScrollQuery): void {
    this.isLoading.set(true);

    // Simulate API call with pagination
    setTimeout(() => {
      const startIndex = query.page * query.pageSize;
      const endIndex = startIndex + query.pageSize;

      let filteredData = [...this.allProducts];

      // Apply search filter
      if (query.search) {
        filteredData = filteredData.filter(
          (product) =>
            product.name.toLowerCase().includes(query.search!.toLowerCase()) ||
            product.description
              .toLowerCase()
              .includes(query.search!.toLowerCase()) ||
            product.sku?.toLowerCase().includes(query.search!.toLowerCase()) ||
            product.brand.toLowerCase().includes(query.search!.toLowerCase())
        );
      }

      // Apply category filter
      if (
        query.category &&
        query.category !== 'all' &&
        query.category !== 'Todos'
      ) {
        filteredData = filteredData.filter(
          (product) =>
            product.category.toLowerCase() === query.category!.toLowerCase()
        );
      }

      const pageData = filteredData.slice(startIndex, endIndex);

      // Update products (append for infinite scroll)
      if (query.page === 0) {
        this.products.set(pageData);
      } else {
        this.products.update((current) => [...current, ...pageData]);
      }

      this.totalProducts = filteredData.length;
      this.hasMoreProducts = endIndex < filteredData.length;
      this.isLoading.set(false);
    }, 500); // Simulate network delay
  }

  private initializeAllProducts(): void {
    // This would normally come from an API
    this.allProducts = [
      // ... same mock data but expanded for demonstration
      {
        id: 1,
        name: 'Notebook Dell Inspiron 15',
        description: 'Notebook para uso profissional com processador Intel i7',
        price: 2899.99,
        cost: 2200.0,
        stock: 15,
        minStock: 5,
        maxStock: 50,
        category: 'Eletrônicos',
        brand: 'Dell',
        model: 'Inspiron 15',
        sku: 'NB001',
        barcode: '1234567890123',
        unit: 'un',
        imageUrl: 'assets/products/notebook.svg',
        active: true,
        createdAt: new Date('2024-01-15'),
      },
      // Add more mock products...
      ...Array.from({ length: 100 }, (_, i) => ({
        id: i + 2,
        name: `Produto ${i + 2}`,
        description: `Descrição do produto ${i + 2}`,
        price: Math.random() * 1000 + 50,
        cost: Math.random() * 500 + 25,
        stock: Math.floor(Math.random() * 100),
        minStock: 5,
        maxStock: 50,
        category: ['Eletrônicos', 'Roupas', 'Casa', 'Esporte'][
          Math.floor(Math.random() * 4)
        ],
        brand: ['Dell', 'Samsung', 'Nike', 'Adidas'][
          Math.floor(Math.random() * 4)
        ],
        model: `Modelo ${i + 2}`,
        sku: `SKU${String(i + 2).padStart(3, '0')}`,
        barcode: `123456789012${i + 2}`,
        unit: 'un',
        imageUrl: `assets/products/product-${(i % 6) + 1}.svg`,
        active: true,
        createdAt: new Date(
          Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000
        ),
      })),
    ];
  }
}
